import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import * as userActions from "../../store/redux/User/actions";
import { toggleMapInSearch, resetInteractiveMapListings } from "../../store/redux/Property/actions";
import Logo from "../../components/Icons/Logo/Logo";
import { AiOutlineMenu } from "react-icons/ai";
import {
  FiGrid,
  FiCompass,
  FiSearch,
  FiMap,
  FiBriefcase,
  FiUsers,
  FiCalendar,
  FiBarChart2,
  FiBookOpen,
  FiMail,
  FiPlayCircle,
  FiFolder,
  FiHelpCircle,
  FiZap,
  FiLayers,
  FiStar,
  FiHeart,
  FiTrendingUp,
  FiShield,
  FiFolderPlus,
  FiGlobe,
  FiMessageSquare,
  FiDatabase,
  FiUser,
  FiLogOut,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { updateSearchValues } from "../../store/redux/Property/actions";
import "./sidebar.scss";
import {
  PATH_LOGIN,
  PATH_SIGNOUT,
  PATH_ADMIN,
  PATH_DESTINATION_COUNTS,
  PATH_CLIENTS,
  PATH_FAQ,
  PATH_INTOUCH,
  PATH_MAP,
  PATH_PROFILE,
  PATH_REPORTS,
  PATH_RESERVATIONS,
  PATH_SEARCH,
  PATH_DASHBOARD,
  PATH_SHUB,
  PATH_COLLECTIONS,
  PATH_WISH_LIST,
  PATH_FAVORITES,
  PATH_HOT_DESTINATIONS,
  PATH_FAVORITES_ADMIN,
  PATH_ENQUIRIES_ADMIN,
  PATH_MEDIA_DIRECTORY,
  PATH_MEDIA_DIRECTORY_ADMIN,
  PATH_GETTING_STARTED,
} from "../../Util/constants";
import ReactGA from "react-ga4";
import { roleOf } from "../../Util/session";
import { sidebarWidth, writeSidebarCollapsed } from "../../Util/sidebarPrefs";
import { clearSearchSelection } from "../../pages/SearchProperty/selectionStore";

const Sidebar = ({ activeMenu, setActiveMenu, handleToggleMenu }) => {
  // Wide <-> icon rail. Held in the user store, not here, because the header's three-lines
  // button and the guided tour set it too (VT review 7/8); remembered for the next visit below.
  const collapsed = useSelector((state) => Boolean(state.user?.settings?.SideBarCollapsed));

  const dispatch = useDispatch();
  const setCollapsed = (next) =>
    dispatch(userActions.setSideBarCollapsed(typeof next === "function" ? next(collapsed) : next));
  const history = useHistory();
  const location = useLocation();
  const screenSize = localStorage.getItem("screenSize");
  const agent = JSON.parse(localStorage.getItem("agent"));
  // Same role reading as the route guards (Util/session: trimmed, lower-cased), so a link is
  // shown exactly when its RoleRoute would let the user in.
  const role = roleOf();
  const isAdmin = role === "admin";
  const isMobile = screenSize < 800;

  // Publish the sidebar width to the document so `.page-body` can shift with
  // the rail, and persist the collapse choice.
  useEffect(() => {
    document.documentElement.style.setProperty("--sidebar-w", sidebarWidth(collapsed));
    writeSidebarCollapsed(collapsed);
  }, [collapsed]);

  const signOut = () => {
    const currentCompany = localStorage.getItem("currentCompany");
    const companyConfig = localStorage.getItem("companyConfig");

    localStorage.clear();
    // The brochure selection lives in sessionStorage, which the line above does
    // not touch. It must not be waiting, ticked, for whoever signs in next.
    clearSearchSelection();

    if (currentCompany) {
      localStorage.setItem("currentCompany", currentCompany);
    }
    if (companyConfig) {
      localStorage.setItem("companyConfig", companyConfig);
    }

    dispatch(userActions.signOut());
    history.push(PATH_LOGIN);
  };

  // -------------------------------------------------------------------------
  // Navigation (preserves the old doPress special cases exactly)
  // -------------------------------------------------------------------------
  const navigate = (path) => {
    if (isMobile) handleToggleMenu(false);
    if (path === PATH_SIGNOUT) {
      signOut();
      return;
    }
    if (path === PATH_MAP) {
      // NB: toggleMapInSearch INVERTS its argument (actions.js) — (false) stores TRUE.
      // The /map view is gated on this flag, so it must be on to render at all.
      dispatch(toggleMapInSearch(false));
      dispatch(resetInteractiveMapListings());
      localStorage.removeItem("dateFrom");
      localStorage.removeItem("dateTo");
      dispatch(updateSearchValues({ dateFrom: "", dateTo: "" }));
      history.push(PATH_MAP);
    } else {
      // Deliberately does NOT touch ShowMapInSearch. This used to dispatch
      // toggleMapInSearch(true) — which STORES false — for every non-map path,
      // /search included. That silently turned the map off on every "Search" click and
      // would defeat the on-by-default initial state. The flag is only read on /search
      // and /map, so there is nothing to reset here; the header checkbox is the user's
      // control, and leaving it alone also means their choice survives navigation.
      history.push(path);
    }
    if (path === PATH_SEARCH) {
      ReactGA.event({ category: "User", action: "Clicked SEARCH" });
    }
  };

  const isActive = (path) => location.pathname === path;

  // -------------------------------------------------------------------------
  // Menu model
  // -------------------------------------------------------------------------
  const dashboardItem = { text: "Dashboard", path: PATH_DASHBOARD, icon: FiGrid, tourId: "sidebar-dashboard" };
  const shubItem = { text: "Shub Panel", path: PATH_SHUB, icon: FiDatabase };
  const profileItem = { text: "Profile", path: PATH_PROFILE, icon: FiUser, tourId: "sidebar-profile" };
  const signoutItem = { text: "Sign Out", path: PATH_SIGNOUT, icon: FiLogOut, danger: true };

  const MENU = [
    {
      id: "explore",
      label: "Explore",
      icon: FiCompass,
      items: [
        { text: "Search", path: PATH_SEARCH, icon: FiSearch, tourId: "sidebar-search" },
        { text: "Interactive Map", path: PATH_MAP, icon: FiMap, tourId: "sidebar-map" },
      ],
    },
    {
      id: "workspace",
      label: "My Workspace",
      icon: FiBriefcase,
      items: [
        { text: "Clients", path: PATH_CLIENTS, icon: FiUsers, tourId: "sidebar-clients" },
        { text: "Reservations", path: PATH_RESERVATIONS, icon: FiCalendar, tourId: "sidebar-reservations" },
        { text: "Reports", path: PATH_REPORTS, icon: FiBarChart2, tourId: "sidebar-reports" },
      ],
    },
    {
      id: "resources",
      label: "Resources",
      icon: FiBookOpen,
      items: [
        { text: "Get In Touch", path: PATH_INTOUCH, icon: FiMail, tourId: "sidebar-touch" },
        { text: "Getting Started", path: PATH_GETTING_STARTED, icon: FiPlayCircle, tourId: "sidebar-getting-started" },
        { text: "Media Directory", path: PATH_MEDIA_DIRECTORY, icon: FiFolder, tourId: "sidebar-media-directory" },
        { text: "FAQs", path: PATH_FAQ, icon: FiHelpCircle, tourId: "sidebar-faqs" },
      ],
    },
    {
      id: "quick",
      label: "Quick Access",
      icon: FiZap,
      items: [
        { text: "Collections", path: PATH_COLLECTIONS, icon: FiLayers, mobileOnly: true },
        { text: "Wish-List", path: PATH_WISH_LIST, icon: FiStar },
        { text: "Favorites", path: PATH_FAVORITES, icon: FiHeart, mobileOnly: true },
        { text: "Hot Destinations", path: PATH_HOT_DESTINATIONS, icon: FiTrendingUp, mobileOnly: true },
      ],
    },
    {
      id: "admin",
      label: "Administration",
      icon: FiShield,
      gated: "admin",
      items: [
        { text: "Admin", path: PATH_ADMIN, icon: FiShield, tourId: "sidebar-admin" },
        { text: "Favourites", path: PATH_FAVORITES_ADMIN, icon: FiHeart, tourId: "sidebar-favourites-admin" },
        { text: "Media Directory (Admin)", path: PATH_MEDIA_DIRECTORY_ADMIN, icon: FiFolderPlus, tourId: "sidebar-media-directory-admin" },
        { text: "Destinations (Admin)", path: PATH_DESTINATION_COUNTS, icon: FiGlobe, tourId: "sidebar-destinations-admin" },
        { text: "Enquiries", path: PATH_ENQUIRIES_ADMIN, icon: FiMessageSquare, tourId: "sidebar-enquiries-admin" },
      ],
    },
  ];

  const visibleItems = (group) =>
    group.items.filter(
      (it) => (!it.role || role === it.role) && (!it.mobileOnly || isMobile)
    );

  // -------------------------------------------------------------------------
  // Renderers
  // -------------------------------------------------------------------------
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <button
        key={item.text}
        type="button"
        className={`sb-item${active ? " active" : ""}${item.danger ? " danger" : ""}`}
        data-tooltip={item.text}
        title={item.text}
        aria-current={active ? "page" : undefined}
        data-tour-id={item.tourId}
        onClick={() => navigate(item.path)}
      >
        <span className="sb-item-icon">
          <Icon size={18} />
        </span>
        <span className="sb-item-text">{item.text}</span>
        {active && <span className="sb-item-indicator" aria-hidden="true" />}
      </button>
    );
  };

  const renderGroup = (group) => {
    if (group.gated && role !== group.gated) return null;
    const items = visibleItems(group);
    if (items.length === 0) return null;
    const GroupIcon = group.icon;
    return (
      <div className="sb-group" key={group.id}>
        <div className="sb-group-label">
          {GroupIcon && <GroupIcon size={13} className="sb-group-label-icon" />}
          <span>{group.label}</span>
        </div>
        <div className="sb-group-items">
          {items.map((it) => renderNavItem(it))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={
        activeMenu
          ? `sidebar-container${collapsed && !isMobile ? " collapsed" : ""}`
          : "sidebar-container-sm"
      }
      style={activeMenu ? {} : screenSize < 800 ? { height: "37px", width: "41px", left: "10px", top: "40px" } : { display: "none" }}
      id="sidebar"
    >
      {!activeMenu && screenSize < 800 && (
        <div onClick={handleToggleMenu} className="h-100">
          <button
            className="p-2 rounded text-white center-btn"
            style={{ background: "none", border: "none", cursor: "pointer" }}
            aria-label="Open menu"
          >
            <AiOutlineMenu color="#000" size={25} style={{ top: "2px" }} />
          </button>
        </div>
      )}

      {activeMenu && (
        <>
          {/* Header: logo + collapse chevron */}
          <div className="sb-header">
            <div className="sb-logo" onClick={() => history.push(PATH_SEARCH)}>
              <Logo />
            </div>
            {!isMobile && (
              <button
                type="button"
                className="sb-collapse-btn"
                aria-expanded={!collapsed}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                title={collapsed ? "Expand" : "Collapse"}
                onClick={() => setCollapsed((c) => !c)}
              >
                {collapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
              </button>
            )}
          </div>

          {/* Nav (scrollable) */}
          <nav className="sb-nav" aria-label="Main">
            {renderNavItem(dashboardItem)}
            {MENU.map(renderGroup)}
            {/* AUTH-08: the route now admits admin and tal, so show the link to both */}
            {(role === "tal" || isAdmin) && renderNavItem(shubItem)}
          </nav>

          {/* Footer (pinned) */}
          <div className="sb-footer">
            {renderNavItem(profileItem)}
            {renderNavItem(signoutItem)}
          </div>
        </>
      )}
    </div>
  );
};

export default Sidebar;
